import { create } from 'zustand';
import { clearQueue } from '@/shared/lib/changeQueue';
import { useReferralStore } from './useReferralStore';

// NOTE: No persist() wrapper here — session state lives in better-auth httpOnly cookie,
// NOT localStorage. Storing session tokens in localStorage is an XSS exfiltration risk (T-03-15).
// The store mirrors ONLY userId and expiry metadata for UI use — the raw session token
// is intentionally excluded (WR-03: token in Zustand is accessible to XSS payloads via
// window.__zustand_state__ even though the httpOnly cookie is protected).
//
// Phase 8: plan fields are UX-only — they are re-fetched from GET /api/user/plan on session
// load and reset on logout (T-08-17). The real authorization gate is server-side (requirePro,
// 08-01). Zustand plan state must NEVER be used for resource access decisions (T-08-16).
//
// Phase 16: access/entitled/trialEndsAt are UX-only mirrors of the server entitlement state
// (T-08-16). Server requireAccess middleware (Phase 15) is the real authorization gate.
// These fields are NEVER used for resource access decisions — only for UX affordances
// (locked cards, trial banners). A user flipping entitled in devtools only unlocks local UX
// — the server still returns 402 on any protected write (T-16-01, T-16-02).

interface AuthState {
  userId: string | null;
  expiresAt: Date | null; // expiry metadata for "session expiring soon" UI warnings
  guestMode: boolean;
  authLoading: boolean; // true until first session check resolves (Phase 12 — Pitfall 3 timing fix)
  // Phase 16 CR-01 — planLoaded: true once plan fetch has settled (success or fail-open).
  // RequireAuth waits on both authLoading===false AND planLoaded===true before rendering
  // PaywallPage, preventing the access:'none'-default flash on every page load.
  planLoaded: boolean;
  // Phase 8 — plan state (UX only, never an authorization source)
  plan: 'free' | 'pro';
  planStatus: string | null; // 'active' | 'trialing' | 'canceled' | 'past_due' | null
  currentPeriodEnd: number | null; // Unix timestamp (seconds)
  // Phase 16 — entitlement state (UX only, never an authorization source — T-08-16)
  access: 'trial' | 'entitled' | 'none';
  entitled: boolean; // derived: access !== 'none' — convenience flag for component props
  trialEndsAt: number | null; // Unix MILLISECONDS (from GET /api/user/plan → entitlement.ts TRIAL_MS convention)
  // Phase 27 — UX-only mirror of server rewardDaysRemaining (T-08-16 convention).
  // Populated from GET /api/user/plan on session load; cleared on logout.
  // NEVER used for authorization — server requireAccess is the real gate.
  // Devtools flip only changes badge text; server still returns 402 on protected writes.
  rewardDaysRemaining: number | null;
  // Phase 43 (PWD-08) — UX-only mirror of server hasPassword (T-08-16 convention).
  // Populated from GET /api/user/plan on session load; reset to null on logout so the
  // next session re-learns it. `null` means "not yet known" and MUST suppress the
  // password-nudge banner exactly like `false` would NOT — the banner only shows when
  // this is the LITERAL boolean `false` (PasswordNudgeBanner), never on `null`.
  // NEVER used for authorization — the server remains the real gate; a user flipping
  // this in devtools only changes whether a nudge/CTA reads "set" vs "change" locally.
  hasPassword: boolean | null;
  // Phase 43 (PWD-06) — true once this tab's useAuth listener learns, via a foreign-origin
  // `session-revoked` broadcast on the shared estimatix-auth channel, that the server has
  // already killed this session elsewhere (a password reset or change made in another tab
  // or on another device — T-43-18). SessionRevokedOverlay renders a full-screen blocking
  // notice whenever this is true.
  // Deliberately EXCLUDED from clearUser's reset block below: the session check that
  // immediately follows a real revocation finds no session and calls clearUser(), and
  // resetting this flag there would erase the notice a fraction of a second after it
  // appeared — dropping the user onto the sign-in screen with no explanation, the exact
  // silent continuation PWD-06 exists to prevent (T-43-23). The ONLY thing that clears it
  // is the full page load SessionRevokedOverlay's call to action performs.
  sessionRevoked: boolean;
  setUser: (userId: string, expiresAt: Date) => void;
  clearUser: () => void;
  setPlan: (plan: 'free' | 'pro', status: string | null, periodEnd: number | null) => void;
  setAuthResolved: () => void; // signals session-check complete (guest or logged-in path)
  setAccess: (access: 'trial' | 'entitled' | 'none', trialEndsAt: number | null) => void;
  setPlanLoaded: () => void; // CR-01: signals plan fetch settled so RequireAuth stops spinning
  setRewardDays: (days: number | null) => void; // Phase 27: UX-only mirror of server rewardDaysRemaining
  setHasPassword: (value: boolean | null) => void; // Phase 43 (PWD-08): UX-only mirror of server hasPassword
  setSessionRevoked: (value: boolean) => void; // Phase 43 (PWD-06): flips the global blocking overlay
  // WR-04 — read-only soft-lock: session flag (non-persisted) set when the user clicks
  // "View projects (read-only)" on PaywallPage. RequireAuth passes through to children
  // when access==='none' && readOnlyPreview. A sticky banner in the app communicates
  // the read-only state and provides an upgrade CTA (T-16-04: server 402 is the real gate).
  readOnlyPreview: boolean;
  setReadOnlyPreview: (value: boolean) => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  userId: null,
  expiresAt: null,
  guestMode: true, // DEFAULT — app is always usable without auth (BLOCKING: guest-first)
  authLoading: true, // DEFAULT — true until first session check resolves; prevents premature ServiceMigration nameKey healing (T-12-03)
  // Phase 16 CR-01: planLoaded defaults false so RequireAuth waits for plan fetch before deciding
  // whether to show Paywall. Reset to false on logout so next login re-waits for plan.
  planLoaded: false,
  plan: 'free',
  planStatus: null,
  currentPeriodEnd: null,
  // Phase 16 — safe defaults: no access until server confirms (T-16-03: conservative default
  // means a failed plan fetch shows the paywall, never grants unbounded access).
  access: 'none',
  entitled: false,
  trialEndsAt: null,
  rewardDaysRemaining: null,
  hasPassword: null,
  sessionRevoked: false,
  setUser: (userId, expiresAt) => set({ userId, expiresAt, guestMode: false, authLoading: false }),
  clearUser: () => {
    // AUTH-04: Clear IDB SyncQueue BEFORE resetting userId to prevent cross-user contamination.
    // Order matters: if clearUser ran first, userId becomes null but the queue would still hold
    // records belonging to the previous user — next user's flush() would upload them under
    // the new session. Fire-and-forget; clearUser is synchronous by design.
    void clearQueue().catch(console.warn);
    // Phase 25: clear pending referral code so a stale invite cannot leak to the next session
    useReferralStore.getState().clearReferral();
    // IN-01: authLoading: false is explicit here — the session IS resolved (user is logging out
    // from an established session). Do NOT set to true; callers are not required to call
    // setAuthResolved() after clearUser(). Setting false makes clearUser self-contained and
    // eliminates the latent hazard where a future caller without the paired setAuthResolved()
    // call could allow ServiceMigration to run prematurely (IN-01 fix).
    set({
      userId: null, expiresAt: null, guestMode: true, plan: 'free', planStatus: null,
      currentPeriodEnd: null, authLoading: false,
      // Phase 16 additions — reset entitlement on logout (T-16-01)
      // CR-01: reset planLoaded so next login re-waits for plan fetch before showing Paywall.
      // WR-04: reset readOnlyPreview so next login starts fresh.
      access: 'none', entitled: false, trialEndsAt: null, planLoaded: false, readOnlyPreview: false,
      rewardDaysRemaining: null,
      // Phase 43 (PWD-08): reset so the next session re-learns hasPassword from the server.
      hasPassword: null,
    });
  },
  setPlan: (plan, planStatus, currentPeriodEnd) => set({ plan, planStatus, currentPeriodEnd }),
  // setAuthResolved: called in the guest branch of useAuth (else if !isPending) to signal
  // that the first session-check is complete and the user is confirmed as a guest.
  // Intentionally a separate action from clearUser — clearUser has AUTH-04 queue clearing
  // responsibility; mixing both would couple unrelated concerns (OQ-6).
  setAuthResolved: () => set({ authLoading: false }),
  // Phase 16 — setAccess mirrors GET /api/user/plan's access/trialEndsAt into UX state.
  // entitled is derived: access !== 'none' (trial and entitled both grant full Pro UX — D-05).
  // This action is NEVER used for authorization — server requireAccess is the real gate (T-08-16).
  // CR-01: setAccess also sets planLoaded:true — signals that plan fetch has settled.
  setAccess: (access, trialEndsAt) => set({ access, trialEndsAt, entitled: access !== 'none', planLoaded: true }),
  // CR-01: setPlanLoaded called on plan-fetch failure so RequireAuth doesn't spin indefinitely.
  // Fail-open on client: planLoaded becomes true with access:'none' → Paywall shown.
  // Server requireAccess (Phase 15) is the real gate — client conservatism is acceptable (T-16-03).
  setPlanLoaded: () => set({ planLoaded: true }),
  // Phase 27: setRewardDays mirrors rewardDaysRemaining from GET /api/user/plan (UX-only, T-08-16).
  // Conservative default (null) means no bonus pill shown until server confirms the reward.
  setRewardDays: (days) => set({ rewardDaysRemaining: days }),
  // Phase 43 (PWD-08): setHasPassword mirrors hasPassword from GET /api/user/plan
  // (UX-only, T-08-16 convention). Conservative default (null) suppresses the
  // password-nudge banner until the server explicitly confirms `false`.
  setHasPassword: (value) => set({ hasPassword: value }),
  // Phase 43 (PWD-06): setSessionRevoked flips the flag SessionRevokedOverlay renders from.
  // Called ONLY from useAuth.ts's message-listener effect, with `true`, on a foreign-origin
  // session-revoked broadcast. Never called with `false` from application code — the flag
  // is retired by the full page reload the overlay's own call to action performs, not by a
  // client-side reset (see the field's own doc comment above for why clearUser must not
  // touch it).
  setSessionRevoked: (value) => set({ sessionRevoked: value }),
  // WR-04: readOnlyPreview — session flag for soft-lock preview mode.
  // Never persisted; resets on logout via clearUser.
  readOnlyPreview: false,
  setReadOnlyPreview: (value) => set({ readOnlyPreview: value }),
}));
